import type { ActivitySessionFormat } from "@/db/schema/activities";
import type { ActivityDefinition } from "@/lib/activities";
import { getActivitySessionRules } from "@/lib/activity-config";

export { buildGeneratedSessionTitle } from "@/lib/feed/feed-descriptor";

import type { ActivityPickerItem } from "./activity-session-drawer.constants";

export const addMinutesToTimeString = (time: string, minutesToAdd: number) => {
  const [hoursRaw, minutesRaw] = time.split(":");
  const hours = Number.parseInt(hoursRaw ?? "", 10);
  const minutes = Number.parseInt(minutesRaw ?? "", 10);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return time;

  const total = hours * 60 + minutes + minutesToAdd;
  const minutesInDay = 24 * 60;
  const normalized = ((total % minutesInDay) + minutesInDay) % minutesInDay;
  const nextHours = Math.floor(normalized / 60);
  const nextMinutes = normalized % 60;

  return `${String(nextHours).padStart(2, "0")}:${String(nextMinutes).padStart(2, "0")}`;
};

export const formatCurrencyInput = (minor: number) =>
  minor >= 0 ? (minor / 100).toFixed(2) : "";

export const coerceDecimalToMinor = (value: string) => {
  if (!value) return 0;
  const parsed = Number.parseFloat(value);
  if (Number.isNaN(parsed)) return 0;
  return Math.round(parsed * 100);
};

/** One short line under a picker tile — it has to fit half a phone. */
export const buildActivityPickerSubtitle = (activity: ActivityDefinition) => {
  const rules = getActivitySessionRules(activity);
  const formats = rules?.session.formats ?? [];

  if (rules?.archetype === "scored_match") {
    const playable = formats.filter((format) => format !== "group");
    return playable.length > 0
      ? playable.map(getSessionFormatLabel).join(" · ")
      : "Group · scored";
  }
  return formats.length === 1 && formats[0] !== "group"
    ? `${getSessionFormatLabel(formats[0])} session`
    : "Group session";
};

export const scoreActivitySearchMatch = (
  item: ActivityPickerItem,
  query: string,
) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) return 0;

  const activityName = item.activity.name.toLowerCase();
  const activityId = item.activity.id.toLowerCase();

  if (activityName === normalizedQuery || activityId === normalizedQuery)
    return 0;
  if (activityName.startsWith(normalizedQuery)) return 1;
  if (activityId.startsWith(normalizedQuery)) return 2;
  if (item.searchText.includes(normalizedQuery)) return 3;
  return 4;
};

export const getSessionFormatLabel = (
  format: ActivitySessionFormat | null | undefined,
) => {
  switch (format) {
    case "singles":
      return "Singles";
    case "doubles":
      return "Doubles";
    case "team":
      return "Team";
    case "group":
      return "Group";
    default:
      return "Session";
  }
};

export const getParticipantRangeLabel = (min: number, max: number) =>
  min === max ? `${min} people` : `${min}-${max} people`;

export const supportsSkillRequirements = (
  activity: ActivityDefinition | undefined | null,
  format: ActivitySessionFormat | null | undefined,
) => {
  const rules = getActivitySessionRules(activity);
  return Boolean(rules?.result.allowed) && format != null && format !== "group";
};

export const getActivitySkillLabel = (
  activity: ActivityDefinition | undefined | null,
  score: number,
) =>
  activity?.levels.find((level) => level.score === score)?.label ??
  `Level ${score}`;
